import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MonitorSmartphone, LogOut, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, timeAgo } from "@/lib/api";
import { isAdminHost } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Every device signed in to this account (server-side sessions) + "Sign out everywhere"
export function SessionsDialog({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/sessions").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { if (open) load(); }, [open]);

  const revoke = async (s) => {
    try {
      await api.delete(`/auth/sessions/${s.id}`);
      toast.success("That device has been signed out");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const revokeAll = async () => {
    if (!window.confirm("Sign out of every device, including this one?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/sessions/revoke-all");
      toast.success(`Signed out of ${data.revoked} device${data.revoked === 1 ? "" : "s"}`);
      onOpenChange(false);
      await logout();
      navigate(user?.platform_admin && !user?.dealer_id ? (isAdminHost() ? "/" : "/admin") : "/login");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const idle = user?.platform_owner || user?.platform_admin ? "2 hours" : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-lg" data-testid="sessions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold flex items-center gap-2">
            <MonitorSmartphone className="h-5 w-5 text-[#0A8CF2]" /> Where you're signed in
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Every device with an open session on your account. Idle sessions end automatically{idle ? ` after ${idle}` : " after the window your dealership set"}.
          </DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#0A8CF2]" /></div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="sessions-list">
            {rows.map((s) => (
              <div key={s.id} data-testid={`session-${s.id}`} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${s.current ? "border-[#0A8CF2]/50 bg-[#0A8CF2]/10" : "border-slate-800 bg-slate-950"}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{s.device || "Unknown device"}{s.current && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#7FC3FF] font-mono-tech" data-testid="session-current">this device</span>}</div>
                  <div className="text-xs text-slate-500">IP {s.ip} · {s.portal === "admin" ? "admin console" : "dashboard"} · active {timeAgo(s.last_seen_at)} · since {new Date(s.created_at).toLocaleDateString()}</div>
                </div>
                {!s.current && (
                  <button onClick={() => revoke(s)} data-testid={`session-revoke-${s.id}`} title="Sign out this device" className="text-slate-500 hover:text-red-400 shrink-0"><X className="h-4 w-4" /></button>
                )}
              </div>
            ))}
            {rows.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No open sessions.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={revokeAll} disabled={busy} data-testid="sign-out-everywhere-btn" className="border-red-900/50 bg-red-950/30 hover:bg-red-950/60 text-red-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="mr-2 h-4 w-4" /> Sign out everywhere</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
